import type { SxProps, Theme } from '@mui/material'
import { Box } from '@mui/material'
import { Controller } from 'react-hook-form'
import Slider from 'react-rangeslider'
import 'react-rangeslider/lib/index.css'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'

import { GINGIVA_LABELS } from '@/utils/constants'

import type { RootState } from '../store'
import Title from './Title'

interface RangeSliderProps {
  name: keyof FormData
  control: any
  label?: string
  sliderRange?: { value: number; step: number }[]
  step?: number
  className?: string
  sx?: SxProps<Theme>
  onFormValueChange?: (name: string, value: number) => void
  selectedValue?: any
}

const RangeSlider: React.FC<RangeSliderProps> = ({
  name,
  control,
  label,
  sliderRange = [],
  step = 0.1,
  className = '',
  sx = {},
  onFormValueChange,
  selectedValue,
}) => {
  const formValue =
    useSelector((state: RootState) => state.allInOne.form[name]) || 0.5
  const valueToUse = selectedValue ?? formValue

  const horizontalLabels = sliderRange.reduce(
    (acc, { value }) => {
      acc[value] = `${value}mm`
      return acc
    },
    {} as Record<string, string>,
  )

  const possibleValues = sliderRange.map(({ value }) => value)

  const getClosestValue = (value: number) => {
    return possibleValues.reduce((prev, curr) => {
      return Math.abs(curr - value) < Math.abs(prev - value) ? curr : prev
    })
  }

  const location = useLocation()

  const isGingivaCeramic = location.pathname.includes('gingiva/ceramic')

  return (
    <Box mb={4} sx={{ ...sx }}>
      {label && (
        <Title fontSize="16px" pb={2}>
          {label}
        </Title>
      )}
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <Slider
            min={sliderRange?.[0]?.value || 0.5}
            max={sliderRange?.[sliderRange?.length - 1]?.value || 1.5}
            step={step}
            value={valueToUse}
            labels={!isGingivaCeramic ? horizontalLabels : GINGIVA_LABELS}
            orientation="horizontal"
            className={`slider ${className}`}
            format={(v) => v.toFixed(1)}
            onChange={(val) => {
              const closestValue = getClosestValue(val)
              field.onChange(closestValue)
              onFormValueChange?.(name, closestValue)
            }}
          />
        )}
      />
    </Box>
  )
}

export default RangeSlider
